import axios from "axios";
import { useAuthStore } from "@/lib/auth/authStore";
import { CONTEXT_HEADERS } from "@/lib/config/constants";
import { useFilialeContextStore } from "@/lib/stores/filialeContext.store";
import { usePermissionStore } from "@/lib/stores/permissions.store";

/**
 * Axios instance with base configuration for API Platform.
 */
const api = axios.create({
  baseURL: "/api",
  timeout: 30000,
  headers: {
    Accept: "application/ld+json",
    "Content-Type": "application/ld+json",
  },
});

// Strip legacy "/api/" prefixes — baseURL already provides "/api".
// Transition aid: makes old prefixed constants resolve correctly while
// they migrate to prefix-free form. Regressions stay visible via the dev warn.
const normalizePath = (url: string) => url.replace(/^\/api\//, "/");

/**
 * Refresh token lock — prevents multiple simultaneous refresh attempts.
 */
let isRefreshing = false;
let refreshSubscribers: Array<(token: string | null) => void> = [];

const addRefreshSubscriber = (callback: (token: string | null) => void) => {
  refreshSubscribers.push(callback);
  return () => {
    refreshSubscribers = refreshSubscribers.filter((cb) => cb !== callback);
  };
};

const onRefreshComplete = (token: string | null) => {
  refreshSubscribers.forEach((callback) => callback(token));
  refreshSubscribers = [];
};

/**
 * Request interceptor — adds auth token and sets proper Content-Type headers.
 */
api.interceptors.request.use(
  (config) => {
    if (config.url?.startsWith("/api/")) {
      config.url = normalizePath(config.url);
      if (import.meta.env.DEV) {
        console.warn("[api] URL with /api/ prefix stripped:", config.url);
      }
    }

    config.headers = config.headers || new axios.AxiosHeaders();

    const token = useAuthStore.getState().token;
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Inject filiale context headers (X-Context-Filiales / X-Context-Dept).
    // Mode "own" → no header → backend uses the user's own filiale.
    // Mode "multi" → selected codes ; mode "all" → full accessible_filiales
    // (option b — the backend has no "absent header = all" wildcard).
    const filialeContext = useFilialeContextStore.getState();
    if (filialeContext.mode === "multi" || filialeContext.mode === "all") {
      const codes =
        filialeContext.mode === "multi"
          ? filialeContext.selectedFilialeCodes
          : usePermissionStore.getState().context.accessible_filiales;
      if (codes.length > 0) {
        config.headers[CONTEXT_HEADERS.FILIALES] = codes.join(",");
      }
    }

    if (filialeContext.selectedDeptPath) {
      config.headers[CONTEXT_HEADERS.DEPT] = filialeContext.selectedDeptPath;
    }

    if (config.method === "post") {
      config.headers["Content-Type"] = "application/ld+json";
    } else if (config.method === "patch") {
      config.headers["Content-Type"] = "application/merge-patch+json";
    } else if (config.method === "get") {
      if (!config.headers.get("Accept")) {
        config.headers.setAccept("application/ld+json");
      }
    }

    return config;
  },
  (error) => {
    console.error("Request error:", error);
    return Promise.reject(error);
  },
);

/**
 * Response interceptor — handles errors and token refresh.
 */
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (!error.response) {
      console.error("Network error - please check your connection");
      return Promise.reject({
        ...error,
        message: "Erreur réseau - Vérifiez votre connexion internet",
      });
    }

    const { status } = error.response;

    if (status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          addRefreshSubscriber((newToken) => {
            if (newToken) {
              originalRequest.headers.Authorization = `Bearer ${newToken}`;
              resolve(api(originalRequest));
            } else {
              reject(error);
            }
          });
        });
      }

      const refreshToken = useAuthStore.getState().refreshToken;

      if (!refreshToken) {
        console.warn("[TokenRefresh] No refresh token available, logging out");
        useAuthStore.getState().logout();
        usePermissionStore.getState().clear();
        if (typeof window !== "undefined") {
          window.location.href = "/auth/login";
        }
        return Promise.reject(error);
      }

      isRefreshing = true;

      try {
        const response = await axios.post(
          "/api/token/refresh",
          { refresh_token: refreshToken },
          { headers: { "Content-Type": "application/json" } },
        );
        const { token, refresh_token } = response.data;

        useAuthStore.getState().login(token, refresh_token);
        onRefreshComplete(token);
        isRefreshing = false;

        originalRequest.headers.Authorization = `Bearer ${token}`;
        return api(originalRequest);
      } catch (refreshError) {
        const typedRefreshError = refreshError as {
          response?: { status?: number; data?: unknown };
        };
        console.error("[TokenRefresh] Failed", {
          status: typedRefreshError.response?.status,
          data: typedRefreshError.response?.data,
        });
        onRefreshComplete(null);
        isRefreshing = false;

        useAuthStore.getState().logout();
        usePermissionStore.getState().clear();

        if (typeof window !== "undefined") {
          window.location.href = "/auth/login";
        }
        return Promise.reject(error);
      }
    }

    if (status === 403) {
      console.error("Access forbidden - insufficient permissions");
      return Promise.reject({
        ...error,
        message: "Accès interdit - Permissions insuffisantes",
      });
    }

    if (status === 404) {
      console.error("Resource not found");
      return Promise.reject({
        ...error,
        message: "Ressource non trouvée",
      });
    }

    if (status === 422) {
      console.error("Validation error:", error.response.data);
      return Promise.reject({
        ...error,
        message: "Erreur de validation",
        validationErrors: error.response.data?.violations || [],
      });
    }

    if (status >= 500) {
      console.error("Server error:", error);
      return Promise.reject({
        ...error,
        message: "Erreur serveur - Veuillez réessayer plus tard",
      });
    }

    return Promise.reject(error);
  },
);

export default api;
